import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { observable, action, computed } from 'mobx';

import { Tab, TabHeight } from 'legacyComponents/styled/lib';
import { NoDataCard } from 'legacyComponents/cards/types/noData';
import { ButtonGroup } from 'legacyComponents/styles';

import Button from 'components/Button';
import Spinner from 'components/Spinner';

import { analytics } from 'core/analytics';

import { clientStore } from 'apps/client/app';

import { ColumnTitles } from 'apps/repository/views/columnTitles';
import { Row } from 'apps/repository/views/row';

import { FolderInput } from 'apps/repository/views/folderInput';
import { CwdDropzone } from 'apps/repository/views/cwdDropzone';
import { ReturnRow } from 'apps/repository/views/returnRow';
import { VersionWarning } from 'apps/project/views/versionWarning';

import { withNamespaces, Trans } from 'react-i18next';

class FilesTabState {
  @observable dragging = false;
  @action SetDragging = bool => {
    this.dragging = bool;
  };

  @action ResetDragging = () => {
    setTimeout(() => {
      this.dragging = false;
    }, 50);
  };
}

export class DragState {
  @observable draggedItem;
  @observable dragOverItem;
  constructor(manager) {
    this.manager = manager;
  }

  @computed get dragging() {
    return !!this.draggedItem;
  }

  @action DragStart = item => {
    this.draggedItem = item;
  };

  @action DragOver = item => {
    this.dragOverItem = item;
  };

  @action DragEnd = () => {
    if (this.draggedItem && this.dragOverItem) {
      this.manager.MoveFile(this.draggedItem, this.dragOverItem);
    }
    this.draggedItem = null;
    this.dragOverItem = null;
  };
}

@withNamespaces()
@inject('routerStore')
@observer
export class FilesTab extends React.Component {
  constructor(props) {
    super(props);
    const { manager } = props;
    this.filesTabState = new FilesTabState();
    this.dragState = new DragState(manager);
  }

  dragEnter = e => {
    const { SetDragging } = this.filesTabState;
    const dragTypes = e.dataTransfer.types || [];
    SetDragging(dragTypes.includes('Files'));
  };

  render() {
    const { app, manager, project, returnLink, routerStore } = this.props;
    const { dragging, SetDragging, ResetDragging } = this.filesTabState;
    const { repository, params } = manager;

    if (manager.loading || !repository || project === null) {
      return (
        <TabHeight>
          <ColumnTitles />
          <Spinner margin='30px auto' />
        </TabHeight>
      );
    }

    const { cwdFiles } = repository;
    const basepath = params.get('basepath');

    const canContribute =
      project.inSpace.whichTypes.includes('Contribution') && manager.atHead;
    const cardType = !basepath ? 'repo' : 'folder';

    const {
      contributionUpstream,
      hasOpenContribution,
      contributionReady,
      zipArchiveUrl,
    } = repository;
    const { signedInProfile } = clientStore;

    return (
      <div
        ref={ref => {
          this.filesTabState.ref = ref;
        }}
        onDragEnter={this.dragEnter}
      >
        <VersionWarning repoManager={manager} />
        <Tab
          onDrop={() => ResetDragging()}
          noShadow={
            !basepath && cwdFiles.length === 0 && !manager.folderInputVisible
          }
        >
          <CwdDropzone
            handleDragLeave={() => SetDragging(false)}
            addFiles={manager.AddFiles}
            visible={dragging && canContribute && manager.atHead}
          />
          {(cwdFiles.length > 0 ||
            cardType !== 'repo' ||
            manager.folderInputVisible) && <ColumnTitles />}
          {(routerStore.route.name.endsWith('directory') ||
            routerStore.route.name.endsWith('dfile')) && (
            <ReturnRow
              dragState={this.dragState}
              route={returnLink && returnLink.route}
              params={returnLink && returnLink.params}
              content={project.content}
            />
          )}
          {manager.folderInputVisible && (
            <FolderInput manager={manager} onlyRow={cwdFiles.length === 0} />
          )}
          {cwdFiles.map((fileInfo, index) => (
            <Row
              dragState={this.dragState}
              project={project}
              canContribute={canContribute}
              app={app}
              key={index}
              index={index}
              fileInfo={fileInfo}
              last={index + 1 === cwdFiles.length}
            />
          ))}
        </Tab>
        {cwdFiles.length === 0 && !manager.folderInputVisible && !dragging && (
          <NoDataCard type={cardType} owner={canContribute} />
        )}
        {hasOpenContribution && (
          <ButtonGroup>
            <Button
              data-test-id='project-reset-button'
              text={<Trans>Reset</Trans>}
              variant='destructive'
              onClick={manager.ConfirmCancelContribution}
              icon='close'
              outline
            />
            <Button
              data-test-id='project-contribute-button'
              text={<Trans>Contribute</Trans>}
              disabled={!contributionReady}
              onClick={manager.ContributionForm}
              icon='check'
            />
          </ButtonGroup>
        )}
        {!hasOpenContribution && !!zipArchiveUrl && !!signedInProfile && (
          <ButtonGroup>
            <a
              onClick={() => {
                analytics.sendEvent('downloaded-project');
              }}
              href={zipArchiveUrl}
              target='_blank'
              rel='noopener noreferrer'
              download
            >
              <Button icon='download' text={<Trans>Download</Trans>} />
            </a>
          </ButtonGroup>
        )}
        {!hasOpenContribution && !!zipArchiveUrl && !signedInProfile && (
          <ButtonGroup>
            <Button
              icon='download'
              onClick={() =>
                clientStore.routerStore.navigate('home.signIn', {
                  next: clientStore.next(),
                })
              }
              text={<Trans>Download</Trans>}
            />
          </ButtonGroup>
        )}
        {!hasOpenContribution && !!contributionUpstream && !zipArchiveUrl && (
          <ButtonGroup>
            <Button
              text={<Trans>Generating Zip...</Trans>}
              variant='secondary'
              outline
              loading
              disabled
            />
          </ButtonGroup>
        )}
      </div>
    );
  }
}

FilesTab.propTypes = {
  routerStore: PropTypes.object,
  app: PropTypes.object,
  project: PropTypes.object,
  manager: PropTypes.object,
  returnLink: PropTypes.object,
};
